import React, { useState, useEffect, useRef } from 'react';
import { CircularProgress, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { SearchIcon } from '~/assets/icons';
import gmailIntegrationApi, { IEmailMessage, IGmailIntegration } from '~/apis/gmailIntegration';
import { notify } from '~/utils/helper';
import { useAppSelector } from '~/redux/hooks';
import EmailDetailPage from './EmailDetailModal';
import SendEmailModal from '~/pages/admin/crm/details/SendEmailModal';
import PrimaryButton from '~/components/common/primary-button/PrimaryButton';

const MailboxTab = () => {
  const { t } = useTranslation(['admin/settings']);
  const { id: jobId } = useParams<{ id: string }>();
  const { jobDetails } = useAppSelector((state) => state.jobDetails);
  const [messages, setMessages] = useState<IEmailMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const [integrations, setIntegrations] = useState<IGmailIntegration[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [composeOpen, setComposeOpen] = useState(false);

  const primaryContact = jobDetails?.relatedContacts?.primaryContact;
  const secondaryContact = jobDetails?.relatedContacts?.secondaryContact;
  const relatedEmails = [primaryContact?.email, secondaryContact?.email].filter(Boolean) as string[];
  const relatedEmailsKey = relatedEmails.join(',');
  const composeTo = primaryContact?.email || secondaryContact?.email || '';
  const composeName = primaryContact?.email
    ? primaryContact?.name
    : secondaryContact?.name;

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const latestRequestRef = useRef(0);

  useEffect(() => {
    if (integrations.length > 0) {
      const connectedIntegration = integrations.find(
        (integration) => integration.status === 'connected' && integration.provider === 'google'
      );
      if (connectedIntegration) {
        fetchMessages();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrations, relatedEmailsKey]);

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const connectedIntegration = integrations.find(
      (integration) => integration.status === 'connected' && integration.provider === 'google'
    );

    if (!connectedIntegration) {
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchMessages();
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const fetchIntegrations = async () => {
    try {
      const response = await gmailIntegrationApi.getIntegrations();
      if (response.data?.data) {
        setIntegrations(response.data.data);
      }
    } catch (error: any) {
      console.error('Failed to fetch Gmail integrations:', error);
    }
  };

  const fetchMessages = async () => {
    if (relatedEmails.length === 0) {
      latestRequestRef.current++;
      setMessages([]);
      setError(null);
      setLoading(false);
      return;
    }
    const requestId = ++latestRequestRef.current;
    setLoading(true);
    setError(null);
    try {
      const params: { maxResults?: number; from?: string; q?: string } = {
        maxResults: 50,
        from: relatedEmails.join(','),
      };

      if (searchQuery.trim()) {
        params.q = searchQuery.trim();
      }

      const response = await gmailIntegrationApi.listMessages(params);
      if (requestId !== latestRequestRef.current) return;
      if (response.data?.data?.messages) {
        setMessages(response.data.data.messages);
      }
    } catch (error: any) {
      if (requestId !== latestRequestRef.current) return;
      const errorMessage = error?.response?.data?.message || t('mailbox.fetchError');
      setError(errorMessage);
      notify('error', errorMessage);
    } finally {
      if (requestId === latestRequestRef.current) setLoading(false);
    }
  };

  const handleMessageClick = (messageId: string) => {
    setSelectedMessageId(messageId);
  };

  const handleBackToList = () => {
    setSelectedMessageId(null);
    setLoading(true);
    setMessages([]);
    fetchMessages();
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      } else if (diffDays === 1) {
        return t('mailbox.yesterday');
      } else if (diffDays < 7) {
        return date.toLocaleDateString('en-US', { weekday: 'short' });
      } else if (date.getFullYear() === now.getFullYear()) {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      } else {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      }
    } catch (error) {
      return dateString;
    }
  };

  const extractEmailName = (fromString: string): string => {
    if (!fromString) return '';
    const match = fromString.match(/^"?(.+?)"?\s*<(.+)>$/);
    if (match) {
      return match[1] || match[2];
    }
    return fromString;
  };

  const connectedIntegration = integrations.find(
    (integration) => integration.status === 'connected' && integration.provider === 'google'
  );

  if (!connectedIntegration) {
    return (
      <div className="w-full h-full flex flex-col bg-[#071427] text-white p-4">
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('mailbox.notConnected')}
        </Alert>
      </div>
    );
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  if (selectedMessageId) {
    return (
      <div className="w-full h-full flex flex-col bg-[#071427] text-white">
        <EmailDetailPage
          messageId={selectedMessageId}
          jobId={jobId}
          onBack={handleBackToList}
        />
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col bg-[#071427] text-white">
      {error && (
        <div className="p-4">
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        </div>
      )}

      <div className="flex items-center justify-between gap-4 p-4 border-b border-[#111e33]">
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#A2A7B4]" />
          <input
            type="text"
            placeholder="Search messages"
            value={searchQuery}
            onChange={handleSearch}
            className="w-[300px] pl-10 pr-4 py-2 bg-[#071427] border border-[#1f2937] rounded-[27px] text-[#949699] placeholder-[#A2A7B4] focus:outline-none focus:border-[#3b82f6]"
          />
        </div>
        <PrimaryButton
          title={t('mailbox.compose')}
          onClick={() => setComposeOpen(true)}
          disabled={!composeTo}
        />
      </div>

      {relatedEmails.length > 0 ? (
        <div className="px-4 pt-3">
          <Alert severity="info">
            {t('mailbox.showingFrom', { emails: relatedEmails.join(', ') })}
          </Alert>
        </div>
      ) : (
        <div className="px-4 pt-3">
          <Alert severity="warning">{t('mailbox.noContactEmail')}</Alert>
        </div>
      )}

      <div className="flex-1 overflow-auto px-[10px] pt-3 pb-4">
        {loading && messages.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <CircularProgress />
          </div>
        ) : (
          <div className="bg-[#0E264C] rounded-[12px] overflow-hidden">
            <table className="w-full table-fixed">
              <thead className="bg-[#0E264C] sticky top-0 z-10">
                <tr>
                  <th className="px-4 h-[72px] text-left text-base font-semibold text-[#f9fbfe] pl-5 w-[20%]">
                    {t('mailbox.from')}
                  </th>
                  <th className="px-4 h-[72px] text-left text-base font-semibold text-[#f9fbfe] w-[25%]">
                    {t('mailbox.subject')}
                  </th>
                  <th className="px-4 h-[72px] text-left text-base font-semibold text-[#f9fbfe] w-[40%]">
                    {t('mailbox.snippet')}
                  </th>
                  <th className="px-4 h-[72px] text-left text-base font-semibold text-[#f9fbfe] pr-5 w-[15%]">
                    {t('mailbox.date')}
                  </th>
                </tr>
                <tr>
                  <td colSpan={4} className="px-0">
                    <div className="mx-5 border-b border-[#111e33]"></div>
                  </td>
                </tr>
              </thead>
              <tbody>
                {messages.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-[#A2A7B4]">
                      <p>{t('mailbox.noMessages')}</p>
                      {relatedEmails.length > 0 && (
                        <p className="mt-2 text-sm">{t('mailbox.noMessagesHint')}</p>
                      )}
                    </td>
                  </tr>
                ) : (
                  messages.map((message) => {
                    const isUnread = message.labelIds?.includes('UNREAD') ?? false;
                    const textColor = isUnread ? 'text-[#F9FBFE]' : 'text-[#949699]';

                    return (
                      <React.Fragment key={message.id}>
                        <tr
                          className="hover:bg-[#0a1b34] transition-colors cursor-pointer"
                          onClick={() => handleMessageClick(message.id)}
                        >
                          <td className="px-4 py-4 pl-5 max-w-0 overflow-hidden">
                            <span
                              className={`text-sm ${textColor} truncate block`}
                              title={message.from || ''}
                            >
                              {extractEmailName(message.from || '')}
                            </span>
                          </td>
                          <td className="px-4 py-4 max-w-0 overflow-hidden">
                            <span
                              className={`text-sm ${textColor} truncate block`}
                              title={message.subject || t('mailbox.noSubject')}
                            >
                              {message.subject || t('mailbox.noSubject')}
                            </span>
                          </td>
                          <td className="px-4 py-4 max-w-0 overflow-hidden">
                            <span
                              className={`text-sm ${textColor} line-clamp-2 block`}
                              title={message.snippet || ''}
                            >
                              {message.snippet || ''}
                            </span>
                          </td>
                          <td className="px-4 py-4 pr-5 max-w-0 overflow-hidden">
                            <span className={`text-sm ${textColor} whitespace-nowrap`}>
                              {message.date ? formatDate(message.date) : ''}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={4} className="px-0 py-0">
                            <div className="mx-5 border-b border-[#111e33]"></div>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {composeOpen && (
        <SendEmailModal
          isOpen={composeOpen}
          onCancel={() => setComposeOpen(false)}
          onSuccess={() => {
            setComposeOpen(false);
            fetchMessages();
          }}
          customerEmail={composeTo}
          customerName={composeName}
          jobId={jobId}
        />
      )}
    </div>
  );
};

export default MailboxTab;
